<template>
    <div :class="$style.page">
        <header :class="$style.header">
            <a
                href="/"
                :class="$style.logo_link"
                aria-label="Playgama for kids home"
            >
                <Logo mode="default" />
            </a>
        </header>

        <main :class="$style.content">
            <Typography
                is="div"
                type="paragraph"
                size="xs"
                :responsive="false"
                :class="$style.markdown"
            >
                <component :is="markup" />
            </Typography>
            <KidsFooter />
        </main>
    </div>
</template>

<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import { compiler as md } from 'markdown-to-jsx'
import { computed, h } from 'vue'

import Link from '@/components/Link.vue'
import Logo from '@/components/Logo.vue'
import Typography from '@/components/Typography.vue'

import KidsFooter from './components/KidsFooter.vue'
import parents from './content/parents.md?raw'
import privacyForKids from './content/privacy-for-kids.md?raw'
import privacy from './content/privacy.md?raw'
import termsOfUse from './content/terms-of-use.md?raw'

type LegalPage = 'parents' | 'privacy-for-kids' | 'privacy' | 'terms-of-use'

const props = defineProps<{ page: LegalPage }>()

const pageData: Record<LegalPage, { content: string; title: string }> = {
    parents: { content: parents, title: 'Information for Parents' },
    'privacy-for-kids': { content: privacyForKids, title: 'Privacy Info for Kids' },
    privacy: { content: privacy, title: 'Privacy Policy' },
    'terms-of-use': { content: termsOfUse, title: 'Terms of Use' },
}

const currentPage = computed(() => pageData[props.page])
const markup = computed(() =>
    md(currentPage.value.content, {
        createElement: (tag, rawProps, ...children) => {
            const { className: _, ...linkProps } = rawProps

            switch (tag) {
                case 'h1':
                    return h(Typography, { is: tag, type: 'header', size: 'm', ...linkProps }, () => children)
                case 'h2':
                    return h(Typography, { is: tag, type: 'header', size: 'xs', ...linkProps }, () => children)
                case 'a': {
                    const href = linkProps.href as string
                    return h(
                        Link,
                        {
                            ...(href.startsWith('/') ? { to: href } : { externalTo: href }),
                            color: 'purple-500',
                            target: linkProps.target || '_self',
                        },
                        () => children,
                    )
                }
                default:
                    return h(tag, linkProps, children)
            }
        },
    }),
)

useHead(
    computed(() => ({
        title: `${currentPage.value.title} | Playgama Kids`,
        htmlAttrs: { lang: 'en' },
        meta: [{ name: 'robots', content: 'noindex, nofollow' }],
    })),
)
</script>

<style module>
.page {
    min-height: 100svh;
    background: var(--carbon-100);
    color: var(--white);
}

.header,
.content {
    margin-inline: 16px;
}

.header {
    border-bottom: 1px solid var(--white-10);
    display: flex;
    margin-top: 16px;
}

.logo_link {
    align-items: center;
    background: var(--purple-400);
    border-radius: 62px;
    display: inline-flex;
    height: 56px;
    padding: 0 20px;
}

.logo_link svg {
    color: var(--white);
    height: 32px;
    width: 148px;
}

.content {
    padding: 22px 0 64px;
}

.markdown {
    color: var(--white-80);
    font-size: var(--font-size-paragraph-s);
    line-height: var(--line-height-paragraph-s);
    max-width: 960px;
}

.markdown h1 {
    margin: 0 0 24px;
}

.markdown h2 {
    margin: 40px 0 16px;
}

.markdown p,
.markdown ul {
    margin: 0 0 16px;
}

.markdown ul {
    list-style: disc;
    padding-left: 24px;
}

.markdown li {
    margin-bottom: 8px;
}

@media (--desktop-small-plus) {
    .header,
    .content {
        margin-inline: 80px;
    }
}
</style>
